import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  People as PeopleIcon,
  FolderSpecial as ProjectsIcon,
  Business as OrgIcon,
  Person as PersonIcon,
  Announcement as AnnouncementIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import CreateProjectMotdDialog from '../components/CreateProjectMotdDialog';

function MyAdminProjects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [organizations, setOrganizations] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Subscription project-cap block — prominent centered modal (not an inline error) when the
  // governing tier''s max_projects is met. Holds { message, subject_type, subject_name }.
  const [capLimit, setCapLimit] = useState(null);
  const [motdOpen, setMotdOpen] = useState(false);
  const [motdProject, setMotdProject] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newProject, setNewProject] = useState({
    name: '',
    code: '',
    description: '',
    type: 'research',
    owner_type: 'user',
    owner_id: null,
  });

  useEffect(() => {
    loadProjects();
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await apiService.getUserOrganizations();
      const orgs = (response.data?.organizations || response.data || [])
        .map(m => m.organization || m)
        .filter(Boolean);
      setOrganizations(orgs);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      const userId = user?._id || user?.id;

      // Load all projects and user's organizations
      const [projectsResponse, orgsResponse] = await Promise.all([
        apiService.getAllProjects(),
        apiService.getUserOrganizations(),
      ]);

      // getAllProjects resolves to a flat array (walks every page).
      const allProjects = Array.isArray(projectsResponse) ? projectsResponse : [];
      const orgs = orgsResponse.data?.organizations || orgsResponse.data || [];

      // Find org IDs where user is owner
      // API returns { organization: {...}, is_owner: boolean, permissions: {...} }
      const ownedOrgIds = orgs
        .filter(org => org.is_owner)
        .map(org => org.organization?._id || org._id);

      // Filter to projects owned by user directly or by their owned organizations
      const myProjects = allProjects.filter(project => {
        const ownerId = project.owner_id?._id || project.owner_id;
        if (project.owner_type === 'user' && ownerId === userId) return true;
        if (project.owner_type === 'organization' && ownedOrgIds.includes(ownerId)) return true;
        return false;
      });

      setProjects(myProjects);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
  };

  const handleOpenCreateDialog = () => {
    setNewProject({ name: '', code: '', description: '', type: 'research', owner_type: 'user', owner_id: null });
    setCreateError('');
    setCreateDialogOpen(true);
  };

  const handleCreateProject = async () => {
    setCreating(true);
    setCreateError('');
    try {
      const createData = { ...newProject };
      if (!createData.owner_id) {
        delete createData.owner_id;
        createData.owner_type = 'user';
      }
      await apiService.createProject(createData);
      setCreateDialogOpen(false);
      loadProjects();
    } catch (err) {
      const errData = err.response?.data?.error;
      // A subscription project-cap rejection gets the prominent modal (with inherited-from), the
      // same treatment as the Add-User cap; everything else stays an inline dialog error.
      if (err.response?.status === 403 && errData?.code === 'SUBSCRIPTION_CAP_EXCEEDED') {
        setCreateDialogOpen(false);
        setCapLimit(errData);
      } else {
        setCreateError((typeof errData === 'object' ? errData?.message : errData) || err.message || 'Failed to create project');
      }
    } finally {
      setCreating(false);
    }
  };

  const isCreateFormValid = newProject.name.trim() && newProject.code.trim() && newProject.description.trim();

  const sortedProjects = React.useMemo(() => {
    const sorted = [...projects];
    const dir = sortOrder === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      let valA, valB;
      switch (sortBy) {
        case 'name':
          valA = (a.name || '').toLowerCase();
          valB = (b.name || '').toLowerCase();
          return dir * valA.localeCompare(valB);
        case 'owner_type':
          valA = a.owner_type || '';
          valB = b.owner_type || '';
          return dir * valA.localeCompare(valB);
        case 'owner':
          valA = (a.owner_id?.name || a.owner_id?.display_name || '').toLowerCase();
          valB = (b.owner_id?.name || b.owner_id?.display_name || '').toLowerCase();
          return dir * valA.localeCompare(valB);
        case 'status':
          valA = a.status || '';
          valB = b.status || '';
          return dir * valA.localeCompare(valB);
        case 'members':
          return dir * ((a.member_count || 0) - (b.member_count || 0));
        case 'created':
          return dir * (new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
        default:
          return 0;
      }
    });
    return sorted;
  }, [projects, sortBy, sortOrder]);

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      completed: 'info',
      archived: 'default',
      draft: 'warning',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ProjectsIcon />
            <Typography variant="h4" component="h1">
              My Projects
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            New Project
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {projects.length === 0 ? (
          <Alert severity="info">
            You do not own any projects directly or through your organizations.
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel active={sortBy === 'name'} direction={sortBy === 'name' ? sortOrder : 'asc'} onClick={() => handleSort('name')}>
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel active={sortBy === 'owner'} direction={sortBy === 'owner' ? sortOrder : 'asc'} onClick={() => handleSort('owner')}>
                      Owner
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel active={sortBy === 'members'} direction={sortBy === 'members' ? sortOrder : 'asc'} onClick={() => handleSort('members')}>
                      Members
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel active={sortBy === 'status'} direction={sortBy === 'status' ? sortOrder : 'asc'} onClick={() => handleSort('status')}>
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel active={sortBy === 'created'} direction={sortBy === 'created' ? sortOrder : 'asc'} onClick={() => handleSort('created')}>
                      Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedProjects.map((project) => (
                  <TableRow key={project._id}>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {project.name}
                      </Typography>
                      {project.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {project.description.length > 80 ? project.description.substring(0, 80) + '...' : project.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {project.owner_type === 'organization' ? (
                          <>
                            <OrgIcon fontSize="small" color="primary" />
                            <Typography variant="body2">
                              {project.owner_id?.name || project.owner_id?.short_name || 'Organization'}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {project.owner_id?.display_name || project.owner_id?.name || project.owner_id?.email || 'You'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={project.member_count ?? 0} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={project.status || 'active'}
                        size="small"
                        color={getStatusColor(project.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {project.created_at ? format(new Date(project.created_at), 'MMM dd, yyyy') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Manage members">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => navigate(`/projects/${project._id}/members`)}
                        >
                          <PeopleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send announcement to members">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => { setMotdProject(project); setMotdOpen(true); }}
                        >
                          <AnnouncementIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      {/* Create Project Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Project</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {createError && (
              <Alert severity="error">{createError}</Alert>
            )}
            <TextField
              label="Project Name"
              value={newProject.name}
              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Project Code"
              value={newProject.code}
              onChange={(e) => setNewProject({ ...newProject, code: e.target.value })}
              fullWidth
              required
              helperText="Short unique identifier (e.g. PROJ-001)"
            />
            <TextField
              label="Description"
              value={newProject.description}
              onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={newProject.type}
                onChange={(e) => setNewProject({ ...newProject, type: e.target.value })}
                label="Type"
              >
                <MenuItem value="research">Research</MenuItem>
                <MenuItem value="education">Education</MenuItem>
                <MenuItem value="outreach">Outreach</MenuItem>
                <MenuItem value="personal">Personal</MenuItem>
                <MenuItem value="testing">Testing</MenuItem>
              </Select>
            </FormControl>
            {organizations.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Organization (Optional)</InputLabel>
                <Select
                  value={newProject.owner_id || ''}
                  onChange={(e) => {
                    const orgId = e.target.value;
                    if (orgId) {
                      setNewProject({ ...newProject, owner_type: 'organization', owner_id: orgId });
                    } else {
                      setNewProject({ ...newProject, owner_type: 'user', owner_id: null });
                    }
                  }}
                  label="Organization (Optional)"
                >
                  <MenuItem value="">Personal (no organization)</MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org._id} value={org._id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Assign this project to an organization</FormHelperText>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            disabled={creating || !isCreateFormValid}
          >
            {creating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Subscription project-cap block — prominent centered modal that halts project creation */}
      <Dialog
        open={Boolean(capLimit)}
        onClose={() => setCapLimit(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <BlockIcon color="error" />
          Subscription Limit Reached
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
            {capLimit?.message}
          </Alert>
          {capLimit?.subject_name && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Your subscription limits are inherited from the {capLimit.subject_type || 'organization'}{' '}
              <strong>{capLimit.subject_name}</strong>.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCapLimit(null)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <CreateProjectMotdDialog
        open={motdOpen}
        onClose={() => { setMotdOpen(false); setMotdProject(null); }}
        projectId={motdProject?._id}
        projectName={motdProject?.name}
      />
    </Box>
  );
}

export default MyAdminProjects;
